/**
 * solutionPages.js — Data source for the 12 SEO landing pages.
 * Each slug corresponds to a targeted long-tail keyword.
 */
export const SOLUTION_PAGES = {
  "recuperation-chaleur-groupe-froid": {
    keyword: "Récupération chaleur groupe froid",
    title: "Récupération de chaleur sur groupe froid industriel",
    subtitle: "Transformez la chaleur fatale de vos condenseurs en eau chaude — jusqu'à 65 % de gaz économisé.",
    heroImage: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "cee-technique",
    ficheCEE: "IND-UT-131",
    primeRange: "120 – 520 k€",
    saving: "45 – 65 % de gaz",
    payback: "24 – 36 mois",
    keyPoints: [
      "Récupération de la chaleur au condenseur d'un groupe froid ≥ 100 kW",
      "Échangeur à plaques inox pour production d'eau chaude sanitaire",
      "Éligible fiche CEE IND-UT-131 avec bonification précarité possible",
      "ROI garanti sous 3 ans pour tout process avec besoin ECS > 30°C",
    ],
    equipment: ["Bitzer", "Carrier", "Trane", "CIAT", "Daikin"],
    process: [
      "Audit de la production frigorifique et des besoins ECS",
      "Modélisation thermique du couplage condenseur / échangeur",
      "Chiffrage prime CEE + ROI",
      "Réalisation clé en main avec partenaires RGE",
    ],
  },
  "cee-groupe-froid": {
    keyword: "CEE groupe froid",
    title: "Prime CEE pour remplacement de groupe froid industriel",
    subtitle: "Fluides naturels R290, régulation HP flottante, COP > 4 — jusqu'à 55 % d'électricité économisée.",
    heroImage: "https://images.unsplash.com/photo-1581093588401-fbb62a02f120?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "cee-technique",
    ficheCEE: "BAT-TH-140 / IND-UT-134",
    primeRange: "80 – 350 k€",
    saving: "35 – 55 % d'électricité",
    payback: "28 – 42 mois",
    keyPoints: [
      "Remplacement de groupes R404A / R507 par R290, R744 (CO₂) ou R1234ze",
      "Régulation par HP flottante et compresseurs à vitesse variable",
      "COP nominal ≥ 4 exigé par la fiche PNCEE",
      "Anticipation des interdictions F-Gas 2027-2030",
    ],
    equipment: ["Bitzer", "Carrier", "Danfoss", "Trane", "CIAT", "Daikin"],
    process: [
      "Diagnostic F-Gas des installations existantes",
      "Dimensionnement du nouveau groupe et fluide",
      "Montage CEE + subventions Fonds Chaleur si applicable",
      "Basculement avec zéro arrêt de production",
    ],
  },
  "cee-air-comprime": {
    keyword: "CEE air comprimé",
    title: "Prime CEE pour installation d'air comprimé industrielle",
    subtitle: "Compresseurs à vitesse variable, traitement des fuites, récupération de chaleur — 30 à 45 % d'économies.",
    heroImage: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "cee-technique",
    ficheCEE: "IND-UT-102 / IND-UT-117",
    primeRange: "40 – 180 k€",
    saving: "30 – 45 % d'électricité",
    payback: "18 – 30 mois",
    keyPoints: [
      "Remplacement compresseur mono-vitesse par vitesse variable ≥ 20 kW",
      "Audit métrologique des fuites (souvent 20-30 % de la conso)",
      "Récupérateur air/eau pour valoriser la chaleur du compresseur",
      "Cumul IND-UT-102 + IND-UT-117 possible",
    ],
    equipment: ["Atlas Copco", "BOGE", "Ingersoll Rand", "Kaeser", "Compair"],
    process: [
      "Campagne de mesure débit + pression + fuites (48h à 7j)",
      "Sélection du compresseur et du récupérateur adapté",
      "Chiffrage double prime (IND-UT-102 + IND-UT-117)",
      "Suivi kWh post-travaux via GTB / GTC",
    ],
  },
  "cee-recuperation-chaleur": {
    keyword: "CEE récupération chaleur",
    title: "Prime CEE pour récupération de chaleur industrielle et tertiaire",
    subtitle: "Compresseurs, fours, groupes froids, buées, fumées — chaque source vaut une prime CEE.",
    heroImage: "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "cee-technique",
    ficheCEE: "IND-UT-117 / IND-UT-131 / IND-UT-134",
    primeRange: "60 – 620 k€",
    saving: "20 – 65 % du fossile",
    payback: "18 – 42 mois",
    keyPoints: [
      "Cartographie de toutes les sources de chaleur fatale disponibles",
      "Sélection échangeur adapté : plaques, tubes, batteries",
      "Cumul possible avec Fonds Chaleur ADEME (>1 GWh/an)",
      "Bonification précarité pour les industries en QPV",
    ],
    equipment: ["Alfa Laval", "Kelvion", "SWEP", "Tranter", "APV"],
    process: [
      "Bilan thermique complet du site (méthode ADEME)",
      "Priorisation par kWh cumac × ROI × faisabilité",
      "Étude détaillée de la solution retenue",
      "Instruction du dossier CEE + Fonds Chaleur",
    ],
  },
  "cee-chaudiere-industrielle": {
    keyword: "CEE chaudière industrielle",
    title: "Prime CEE pour chaudière industrielle et brûleur modulant",
    subtitle: "Brûleurs modulants, retour condensats, calorifugeage réseau — jusqu'à 25 % de gaz économisé.",
    heroImage: "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "cee-technique",
    ficheCEE: "IND-UT-108 / IND-UT-121",
    primeRange: "50 – 280 k€",
    saving: "15 – 25 % de gaz",
    payback: "24 – 36 mois",
    keyPoints: [
      "Remplacement brûleur atmosphérique par brûleur modulant à variateur",
      "Économiseur sur fumées pour préchauffer l'eau d'alimentation",
      "Retour de condensats + calorifugeage des points singuliers",
      "Éligibilité étendue aux chaudières vapeur ≥ 1 t/h",
    ],
    equipment: ["Bosch", "Viessmann", "Weishaupt", "Riello", "Cochran"],
    process: [
      "Diagnostic combustion + analyse fumées",
      "Dimensionnement brûleur + économiseur",
      "Choix des points de calorifugeage à traiter (fiche IND-UT-121)",
      "Certification qualité et suivi performance sur 12 mois",
    ],
  },
  "destratification-industrielle": {
    keyword: "Déstratification industrielle",
    title: "Prime CEE pour déstratification en entrepôt et atelier",
    subtitle: "Homogénéisation de l'air chaud sous plafond — 15 à 25 % d'économie chauffage sans travaux lourds.",
    heroImage: "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "renovation",
    ficheCEE: "IND-BA-116",
    primeRange: "15 – 90 k€",
    saving: "15 – 25 % chauffage",
    payback: "12 – 20 mois",
    keyPoints: [
      "Brasseurs d'air installés en toiture pour homogénéiser T°",
      "Réduit l'écart pied/plafond de 8-12°C → 1-2°C",
      "Aucune modification du système de chauffage existant",
      "Installation sans arrêt de production",
    ],
    equipment: ["Big Ass Fans", "Airius", "Sonair", "Nordik", "Zebra"],
    process: [
      "Simulation thermique du bâtiment (CFD)",
      "Positionnement optimal des brasseurs",
      "Installation en 1-3 jours selon surface",
      "Suivi consommation post-installation",
    ],
  },
  "gtb-cee": {
    keyword: "GTB CEE",
    title: "Prime CEE pour Gestion Technique du Bâtiment (GTB / GTC)",
    subtitle: "Pilotage centralisé chauffage, climatisation, éclairage, ventilation — jusqu'à 30 % d'énergie économisée.",
    heroImage: "https://images.unsplash.com/photo-1573164574230-db1d5e960238?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "cee-technique",
    ficheCEE: "BAT-TH-116",
    primeRange: "50 – 220 k€",
    saving: "20 – 30 % toutes énergies",
    payback: "26 – 38 mois",
    keyPoints: [
      "GTB de classe A ou B (NF EN ISO 52120-1)",
      "Prise en compte de 4+ fonctions (chauffage, froid, ventil, éclairage)",
      "Compatible avec le décret tertiaire Éco Énergie Tertiaire",
      "Base solide pour la mesure et vérification de performance",
    ],
    equipment: ["Schneider Electric", "Siemens", "Johnson Controls", "Honeywell", "Distech"],
    process: [
      "Audit du système GTB existant (le cas échéant)",
      "Sélection éditeur + architecture réseau adaptée",
      "Paramétrage des scénarios d'ordonnancement",
      "Formation utilisateurs + hotline 12 mois",
    ],
  },
  "cta-cee": {
    keyword: "CTA CEE",
    title: "Prime CEE pour Centrale de Traitement d'Air haute efficacité",
    subtitle: "CTA double flux avec récupérateur — jusqu'à 40 % de chauffage économisé en tertiaire.",
    heroImage: "https://images.unsplash.com/photo-1462899006636-339e08d1844e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "cee-technique",
    ficheCEE: "BAT-TH-125 / BAT-TH-127",
    primeRange: "30 – 160 k€",
    saving: "30 – 40 % chauffage",
    payback: "22 – 34 mois",
    keyPoints: [
      "Récupérateur rotatif ou à plaques ≥ 80 % de rendement",
      "Moteurs EC + variateur intégré",
      "Régulation sur qualité d'air (CO₂ / COV) — Demand Control",
      "Compatible bureaux, ERP, écoles, hôpitaux",
    ],
    equipment: ["Aldes", "Atlantic", "Systemair", "Swegon", "France Air"],
    process: [
      "Bilan aéraulique du bâtiment (débits, pertes)",
      "Sélection récupérateur (rotatif / plaques / batterie)",
      "Chiffrage BAT-TH-125 (renovation) ou BAT-TH-127 (neuf)",
      "Installation en week-end pour ERP en activité",
    ],
  },
  "hotel-cee": {
    keyword: "Hôtel CEE",
    title: "Prime CEE pour hôtels — chauffage, ECS, ventilation, éclairage",
    subtitle: "Pack complet CEE hôtellerie 3 à 5★ — 30 à 45 % de la facture énergétique récupérée.",
    heroImage: "https://images.unsplash.com/photo-1590490360182-c33d57733427?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "renovation",
    ficheCEE: "BAT-TH-104 / BAT-TH-116 / BAT-TH-127 / BAT-EQ-127",
    primeRange: "80 – 320 k€",
    saving: "30 – 45 % énergie",
    payback: "20 – 32 mois",
    keyPoints: [
      "Pompe à chaleur air/eau pour chauffage + ECS mutualisés",
      "GTB pour ordonnancement chambres et parties communes",
      "CTA double flux dans salles de conférence et restaurants",
      "LED avec détecteurs de présence en circulations",
    ],
    equipment: ["Daikin", "Mitsubishi", "Atlantic", "Panasonic", "Schneider"],
    process: [
      "Audit multi-usage (chauffage, ECS, cuisine, ventil, éclairage)",
      "Prise en compte de la saisonnalité et taux d'occupation",
      "Cumul CEE + Fonds Chaleur sur PAC si Fonds éligible",
      "Réalisation en basse saison avec zéro impact clients",
    ],
  },
  "agroalimentaire-cee": {
    keyword: "Agroalimentaire CEE",
    title: "Prime CEE pour l'industrie agroalimentaire",
    subtitle: "Froid, vapeur, air comprimé, séchage — les leviers CEE de l'IAA les plus rentables.",
    heroImage: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "cee-technique",
    ficheCEE: "IND-UT-102 / IND-UT-117 / IND-UT-131 / IND-UT-134",
    primeRange: "150 – 680 k€",
    saving: "20 – 40 % toutes énergies",
    payback: "24 – 38 mois",
    keyPoints: [
      "Récupération de chaleur sur groupes froids ammoniac / CO₂",
      "Compresseurs à vitesse variable sur salle des machines",
      "Isolation vapeur pour laiteries et abattoirs",
      "Séchage optimisé pour fromageries et biscuiteries",
    ],
    equipment: ["Bitzer", "GEA", "Alfa Laval", "Grasso", "Sabroe"],
    process: [
      "Audit métrologique multi-utilités (48h à 15j)",
      "Priorisation par ROI × faisabilité en environnement HACCP",
      "Montage dossier CEE + demande Fonds Chaleur si éligible",
      "Réalisation avec certifications IFS / BRC préservées",
    ],
  },
  "logistique-cee": {
    keyword: "Logistique CEE",
    title: "Prime CEE pour entrepôts logistiques et plateformes",
    subtitle: "Déstratification, LED, GTB, PAC air/eau — jusqu'à 35 % d'économies sur des surfaces > 10 000 m².",
    heroImage: "https://images.unsplash.com/photo-1553413077-190dd305871c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "renovation",
    ficheCEE: "IND-BA-112 / IND-BA-116 / BAT-EQ-127 / BAT-TH-116",
    primeRange: "60 – 340 k€",
    saving: "20 – 35 % énergie",
    payback: "18 – 28 mois",
    keyPoints: [
      "Déstratification obligatoire pour tout entrepôt chauffé > 5 000 m²",
      "LED avec détecteurs sur circulations racks",
      "Portes rapides pour rupture pont thermique quais",
      "GTB pilotage éclairage + températures par zone",
    ],
    equipment: ["Big Ass Fans", "Ledvance", "Zumtobel", "Efaflex", "Hörmann"],
    process: [
      "Cartographie thermique par zone (bureaux, stockage, quais)",
      "Chiffrage combiné avec économiseur de portes rapides",
      "Réalisation en 3 phases sans impact activité",
      "Suivi kWh post-travaux via smart meters",
    ],
  },
  "entrepot-frigorifique-cee": {
    keyword: "Entrepôt frigorifique CEE",
    title: "Prime CEE pour entrepôts frigorifiques et chambres froides",
    subtitle: "Groupes CO₂, HP flottante, portes rapides, récupération chaleur — jusqu'à 50 % d'électricité.",
    heroImage: "https://images.unsplash.com/photo-1614624532983-4ce03382d63d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    category: "cee-technique",
    ficheCEE: "IND-UT-134 / IND-UT-131 / IND-BA-112",
    primeRange: "120 – 480 k€",
    saving: "30 – 50 % électricité",
    payback: "26 – 40 mois",
    keyPoints: [
      "Remplacement des groupes R404A par groupes CO₂ transcritiques",
      "HP flottante sur groupes existants (économie rapide 8-15 %)",
      "Portes rapides + rideaux à lames sur chambres positives",
      "Récupération chaleur pour ECS bâtiments annexes",
    ],
    equipment: ["Bitzer", "Copeland", "Danfoss", "Advansor", "Green & Cool"],
    process: [
      "Diagnostic F-Gas + audit énergétique",
      "Modélisation basculement CO₂ ou modernisation existant",
      "Chiffrage prime CEE + garantie constructeur 5 ans",
      "Basculement avec plan de continuité chaîne du froid",
    ],
  },
};
